import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import CameraGuiada from "./CameraGuiada";
import DemarcacaoEditor from "./DemarcacaoEditor";
import FichaReferenciaModal from "./FichaReferenciaModal";
import ReferenciasBiblioteca from "./ReferenciasBiblioteca";
import { salvarFotoOffline, listarFotosOffline, removerFotoOffline, isNetworkError } from "../lib/offlineFotos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GUIA_FOTOS = {
  painel: [
    ["📐 Ângulo", "Fotografe o corte inteiro, de frente, celular na altura do painel (não de baixo para cima)."],
    ["🔪 Ferimentos", "Aproxime a 30–50 cm da linha de corte e da casca de regeneração (área acima do corte)."],
    ["📏 Consumo de casca", "Enquadre o painel todo; se usar gabarito/bandeiramento, inclua as marcas na foto."],
    ["📏 Faixa de corte", "Aproxime a 30–40 cm da faixa consumida (linhas de corte acima do corte atual) — nítida para a IA contar linha a linha e medir o consumo por corte."],
    ["📍 Geratriz", "Mostre a linha vertical de demarcação de cima a baixo."],
    ["🥤 Equipamentos", "Inclua bica, caneca e arame no enquadramento."],
    ["💡 Geral", "Luz natural, sem sombra forte, foto nítida. Pode enviar mais de uma análise da mesma árvore."],
  ],
  folhagem: [
    ["🍄 Doença", "Folha com sintoma preenchendo a tela; fotografe frente e verso."],
    ["📊 Severidade", "Envie também uma foto do conjunto (ramo/copa) para estimar a área afetada."],
    ["💡 Geral", "Luz natural, foco na mancha/lesão, fundo o mais limpo possível."],
  ],
};

const FOCOS = {
  painel: [["angulo", "📐 Ângulo"], ["ferimentos", "🔪 Ferimentos"], ["consumo", "📏 Consumo"], ["faixa", "📏 Faixa de corte"], ["equipamentos", "🥤 Equipamentos"], ["geral", "🌳 Geral"]],
  folhagem: [["folha", "🍃 Folha"], ["conjunto", "🌿 Conjunto"]],
};
const FOCO_LABELS = Object.fromEntries([...FOCOS.painel, ...FOCOS.folhagem]);

const ETAPAS_LOADING = [
  "📤 Enviando foto...",
  "🎯 Conferindo o enquadramento...",
  "🔍 Analisando ângulo e corte...",
  "🔪 Verificando ferimentos no câmbio...",
  "🍂 Avaliando fitossanidade...",
  "📋 Montando o diagnóstico...",
];

const STATUS_CORES = {
  adequado: "bg-green-950 text-green-200 border-green-700",
  atencao: "bg-yellow-950 text-yellow-200 border-yellow-700",
  critico: "bg-red-950 text-red-200 border-red-700",
  nao_visivel: "bg-[#1b1b1b] text-gray-400 border-[#444]",
};
const STATUS_LABEL = { adequado: "✅ Adequado", atencao: "⚠️ Atenção", critico: "🔴 Crítico", nao_visivel: "👁️ Não visível" };

const chip = (on, cls) => `flex-shrink-0 rounded-full px-3 py-1.5 text-[11px] font-extrabold border-2 transition-colors ${on ? cls : "border-[#333] bg-[#1b1b1b] text-[#999]"}`;
const SECAO = "text-[10px] font-extrabold tracking-widest text-[#7fa57f] uppercase";
const CARD = "bg-[#151d15] border border-[#2e3e2e] rounded-2xl";
const INPUT = "rounded-lg border border-[#2e3e2e] bg-[#0f150f] text-white placeholder:text-[#777] p-2 text-sm w-full";

const renderBold = (text) =>
  (text || "").split(/\*\*(.*?)\*\*/g).map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));

const resizeImage = (file, max = 1280) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

export default function DiagnosticoIAPanel() {
  const [stats, setStats] = useState(null);
  const [casos, setCasos] = useState([]);
  const [tipo, setTipo] = useState("painel");
  const [contexto, setContexto] = useState("rotina");
  const [clone, setClone] = useState("");
  const [obs, setObs] = useState("");
  const [sistemaSangria, setSistemaSangria] = useState("");
  const [fotos, setFotos] = useState([]);
  const [foco, setFoco] = useState("geral");
  const [complementos, setComplementos] = useState([]);
  const [validando, setValidando] = useState(false);
  const [cameraModoInicial, setCameraModoInicial] = useState(null);
  const [gateErro, setGateErro] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [guiaAberto, setGuiaAberto] = useState(false);
  const [statsAberto, setStatsAberto] = useState(false);
  const [analisando, setAnalisando] = useState(false);
  const [etapa, setEtapa] = useState(0);
  const [caso, setCaso] = useState(null);
  const [correcoes, setCorrecoes] = useState({});
  const [comentario, setComentario] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [filaOffline, setFilaOffline] = useState([]);
  const [modoLivre, setModoLivre] = useState(false);
  const [enviandoFilaId, setEnviandoFilaId] = useState(null);
  const [referencias, setReferencias] = useState([]);
  const [fichaVer, setFichaVer] = useState(null);
  const [showBiblio, setShowBiblio] = useState(false);
  const [demarcPend, setDemarcPend] = useState(null);
  const [demarcAberta, setDemarcAberta] = useState(false);
  const fileRef = useRef(null);

  const carregar = useCallback(() => {
    axios.get(`${API}/admin/diagnostico/stats`).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/admin/diagnostico/casos`).then((r) => setCasos(r.data)).catch(() => {});
    axios.get(`${API}/admin/diagnostico/referencias`).then((r) => setReferencias(r.data)).catch(() => {});
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const carregarFila = useCallback(() => {
    listarFotosOffline("diagnostico").then(setFilaOffline).catch(() => {});
  }, []);
  useEffect(() => {
    carregarFila();
    const on = () => { setOnline(true); carregarFila(); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [carregarFila]);

  useEffect(() => {
    if (!analisando) return;
    const int = setInterval(() => setEtapa((e) => Math.min(e + 1, ETAPAS_LOADING.length - 1)), 4000);
    return () => clearInterval(int);
  }, [analisando]);

  const guardarOffline = async (dataUrl, focoSel, anguloMed, origem = "camera") => {
    try {
      await salvarFotoOffline("diagnostico", { dataUrl, tipo, foco: focoSel, contexto, clone, obs, sistemaSangria, anguloMedido: anguloMed, origem });
      carregarFila();
      toast.info("📴 Sem conexão — foto salva no aparelho para enviar depois");
    } catch { toast.error("Não foi possível salvar a foto no aparelho"); }
  };

  const addFoto = async (dataUrl, focoSel, anguloMed = null, origem = "camera") => {
    if (fotos.length >= 3) { toast.error("Máximo de 3 fotos por análise"); return; }
    setGateErro(null);
    if (modoLivre) {
      setFotos((p) => [...p, { dataUrl, foco: focoSel, anguloMedido: anguloMed, semValidacao: true, origem }]);
      toast.success("🧪 Foto aceita sem validação (modo calibração livre)");
      return;
    }
    if (!navigator.onLine) { await guardarOffline(dataUrl, focoSel, anguloMed, origem); return; }
    setValidando(true);
    try {
      const r = await axios.post(`${API}/admin/diagnostico/validar-foto`, {
        tipo, photo_base64: dataUrl, foco: focoSel, focos_existentes: fotos.map((f) => f.foco),
      }, { timeout: 60000 });
      if (!r.data.aprovada) {
        setGateErro({ motivo: r.data.motivo, orientacao: r.data.orientacao });
        return;
      }
      setFotos((p) => [...p, { dataUrl, foco: focoSel, anguloMedido: anguloMed, origem }]);
      setComplementos(r.data.complementos || []);
      toast.success("✅ Foto aprovada no enquadramento");
    } catch (err) {
      if (isNetworkError(err)) await guardarOffline(dataUrl, focoSel, anguloMed, origem);
      else toast.error(err.response?.data?.detail || "Erro ao validar a foto");
    } finally { setValidando(false); }
  };

  const enviarDaFila = async (item) => {
    if (!navigator.onLine) { toast.error("Ainda sem conexão — tente quando o sinal voltar"); return; }
    if (item.tipo === tipo && fotos.length >= 3) { toast.error("Máximo de 3 fotos por análise — analise ou remova as atuais"); return; }
    if (item.tipo !== tipo) {
      if (fotos.length > 0 && !window.confirm(`Esta foto é de ${item.tipo === "painel" ? "Painel" : "Diagnóstico Foliar"} — as fotos atuais serão substituídas. Continuar?`)) return;
      setTipo(item.tipo); setComplementos([]);
      if (item.contexto) setContexto(item.contexto);
    }
    const livre = modoLivre || !!item.semValidacao;
    setFoco(item.foco);
    if (item.clone) setClone(item.clone);
    if (item.obs) setObs(item.obs);
    if (item.sistemaSangria) setSistemaSangria(item.sistemaSangria);
    setEnviandoFilaId(item.id); setGateErro(null);
    try {
      let comps = [];
      if (!livre) {
        const r = await axios.post(`${API}/admin/diagnostico/validar-foto`, {
          tipo: item.tipo, photo_base64: item.dataUrl, foco: item.foco,
          focos_existentes: item.tipo === tipo ? fotos.map((f) => f.foco) : [],
        }, { timeout: 60000 });
        if (!r.data.aprovada) {
          setGateErro({ motivo: r.data.motivo, orientacao: r.data.orientacao });
          toast.error("Foto reprovada no enquadramento — veja a orientação, exclua ou ative o modo livre");
          return;
        }
        comps = r.data.complementos || [];
      }
      const nova = { dataUrl: item.dataUrl, foco: item.foco, anguloMedido: item.anguloMedido ?? null, semValidacao: livre, origem: item.origem || "galeria" };
      setFotos((p) => (item.tipo !== tipo ? [nova] : [...p, nova]));
      setComplementos(comps);
      await removerFotoOffline(item.id);
      carregarFila();
      toast.success(livre ? "🧪 Foto carregada sem validação" : "✅ Foto aprovada no enquadramento");
    } catch (err) {
      toast.error(isNetworkError(err) ? "Sem conexão — tente novamente" : err.response?.data?.detail || "Erro ao enviar a foto");
    } finally { setEnviandoFilaId(null); }
  };

  const excluirDaFila = async (id) => {
    await removerFotoOffline(id).catch(() => {});
    carregarFila();
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { await addFoto(await resizeImage(f, 2048), foco, null, "galeria"); } catch { toast.error("Não foi possível ler a foto"); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const iniciarCorrecoes = (c) => {
    const map = {};
    if (c.modo === "sanidade") map.diagnostico_sanidade = { ia_acertou: true, correcao: "" };
    else (c.criterios || []).forEach((cr) => { map[cr.id] = { ia_acertou: true, correcao: "" }; });
    setCorrecoes(map);
    setComentario(c.comentario || "");
  };

  const analisar = async () => {
    if (!fotos.length) { toast.error("Envie uma foto primeiro"); return; }
    setAnalisando(true); setEtapa(0); setGateErro(null);
    try {
      const principal = fotos[0];
      const r = await axios.post(`${API}/admin/diagnostico/analisar`, {
        tipo, photo_base64: principal.dataUrl, contexto_corte: contexto, clone, observacao: obs,
        foco: principal.foco,
        angulo_medido: fotos.find((f) => f.anguloMedido != null)?.anguloMedido ?? null,
        fotos_extras: fotos.slice(1).map((f) => ({ photo_base64: f.dataUrl, foco: f.foco })),
        pular_validacao: true,
        sem_validacao: fotos.some((f) => f.semValidacao),
        origem: fotos[0]?.origem || "camera",
        sistema_sangria: tipo === "painel" ? sistemaSangria : "",
      }, { timeout: 180000 });
      let demData = null;
      if (demarcPend && tipo === "painel" && r.data?.id) {
        try {
          const dr = await axios.put(`${API}/admin/diagnostico/casos/${r.data.id}/demarcacao`, demarcPend);
          demData = dr.data;
        } catch { toast.error("Análise ok, mas a demarcação não pôde ser salva"); }
      }
      setCaso({ ...r.data, ...(demData ? { demarcacao: demData.demarcacao, demarcacao_resumo: demData.demarcacao_resumo } : {}), photo_base64: principal.dataUrl.split(",")[1], fotos_extras_local: fotos.slice(1) });
      setDemarcPend(null);
      iniciarCorrecoes(r.data);
      setFotos([]); setComplementos([]); setObs("");
      carregar();
    } catch (err) {
      if (isNetworkError(err)) {
        for (const f of fotos) {
          await salvarFotoOffline("diagnostico", { dataUrl: f.dataUrl, tipo, foco: f.foco, contexto, clone, obs, anguloMedido: f.anguloMedido ?? null, semValidacao: !!f.semValidacao, origem: f.origem || "camera" }).catch(() => {});
        }
        setFotos([]); setComplementos([]);
        carregarFila();
        toast.info("📴 Sem conexão — fotos salvas no aparelho para analisar quando o sinal voltar");
      } else {
        toast.error(err.response?.data?.detail || "Falha na análise. Tente novamente.");
      }
    } finally { setAnalisando(false); }
  };

  const abrirCaso = async (id) => {
    try {
      const r = await axios.get(`${API}/admin/diagnostico/casos/${id}`);
      setCaso(r.data);
      if (r.data.status === "pendente") iniciarCorrecoes(r.data);
    } catch { toast.error("Erro ao abrir caso"); }
  };

  const excluir = async (id) => {
    if (!window.confirm("Excluir este caso?")) return;
    try {
      await axios.delete(`${API}/admin/diagnostico/casos/${id}`);
      toast.success("Caso excluído");
    } catch { toast.error("Erro ao excluir caso"); }
    setCaso(null); carregar();
  };

  const salvarCalibracao = async () => {
    const lista = Object.entries(correcoes).map(([id, v]) => ({ id, ia_acertou: v.ia_acertou, correcao: v.correcao }));
    const incompleto = lista.find((c) => !c.ia_acertou && !c.correcao.trim());
    if (incompleto) { toast.error("Descreva a correção nos critérios marcados como errados"); return; }
    setSalvando(true);
    try {
      const r = await axios.post(`${API}/admin/diagnostico/casos/${caso.id}/corrigir`, { correcoes: lista, comentario });
      toast.success(`Calibração salva — IA acertou ${r.data.acertos}/${r.data.total}`);
      setCaso(null); carregar();
    } catch { toast.error("Erro ao salvar calibração"); }
    finally { setSalvando(false); }
  };

  const salvarDemarcacaoCaso = async (d) => {
    try {
      const r = await axios.put(`${API}/admin/diagnostico/casos/${caso.id}/demarcacao`, d);
      setCaso((p) => ({ ...p, demarcacao: r.data.demarcacao, demarcacao_resumo: r.data.demarcacao_resumo }));
      toast.success("📐 Demarcação salva no caso");
      carregar();
    } catch { toast.error("Erro ao salvar demarcação"); }
    setDemarcAberta(false);
  };

  // ---------- Vista de caso (resultado + correção) ----------
  if (caso) {
    const pendente = caso.status === "pendente";
    const sanidade = caso.modo === "sanidade";
    const fichasPara = (crId) => {
      if (crId === "angulo") {
        const asc = caso?.leitura_painel?.sistema === "ascendente";
        const f = referencias.find((r) => r.id === (asc ? "ref_angulo_ascendente" : "ref_angulo_descendente"));
        return f ? [f] : referencias.filter((r) => r.criterio === "angulo").slice(0, 1);
      }
      if (crId === "fitossanidade") return referencias.filter((r) => ["doenca_painel", "radicular"].includes(r.categoria));
      return referencias.filter((r) => r.criterio === crId);
    };
    const fichasSanidade = referencias.filter((r) =>
      (caso.tipo === "painel" ? ["doenca_painel", "radicular"] : ["foliar", "radicular"]).includes(r.categoria));
    const itensCalibracao = sanidade
      ? [{ id: "diagnostico_sanidade", nome: "🔬 Diagnóstico de sanidade", leitura: (caso.hipoteses || []).map((h) => h.doenca).filter(Boolean).join("; ") || "—", status: null, nota: null }]
      : (caso.criterios || []);
    const extrasCaso = caso.fotos_extras_local
      ? caso.fotos_extras_local.map((f) => ({ foco: f.foco, b64: f.dataUrl.split(",")[1] }))
      : (caso.fotos_extras || []).map((f) => ({ foco: f.foco, b64: f.photo_base64 }));
    return (
      <div className="bg-[#111] text-white rounded-2xl overflow-hidden -mx-1 sm:mx-0" data-testid="diagnostico-caso-view">
        <div className="flex items-center justify-between px-3.5 py-3 bg-[#1a241a]">
          <button onClick={() => { setCaso(null); setDemarcAberta(false); }} className="text-xs font-bold bg-white/10 rounded-full px-3 py-1.5" data-testid="diag-voltar-btn">← Voltar</button>
          <div className="flex gap-2 items-center flex-wrap justify-end">
            {caso.angulo_medido != null && <span className="text-[10px] font-bold bg-blue-950 text-blue-200 border border-blue-700 px-2 py-1 rounded-full" data-testid="diag-caso-angulo-medido">📐 Medido: {caso.angulo_medido}°</span>}
            {caso.nota_geral != null && <span className="text-xs font-bold bg-green-950 text-green-200 border border-green-700 px-2 py-1 rounded-full" data-testid="diag-nota-geral">Nota IA: {caso.nota_geral}/10</span>}
          </div>
        </div>
        <div className="bg-[#2a3a2a] text-[#c9eec9] text-[11px] px-3 py-1.5 font-semibold flex flex-wrap gap-x-3 gap-y-0.5 items-center">
          <span className="font-extrabold">{caso.tipo === "painel" ? "🌳 Painel" : "🍃 Diagnóstico Foliar"}</span>
          {caso.contexto_corte === "teste_fitossanitario" && <span className="text-[10px] bg-purple-950 text-purple-200 border border-purple-700 px-2 py-0.5 rounded-full">🔬 teste fitossanitário</span>}
          {caso.sem_validacao && <span className="text-[10px] bg-purple-950 text-purple-200 border border-purple-700 px-2 py-0.5 rounded-full" data-testid="diag-caso-sem-validacao">🧪 sem validação de enquadramento</span>}
          {caso.confianca && <span className="text-[10px] text-[#9fc29f]">Confiança: {caso.confianca}</span>}
        </div>

        <div className="p-3 space-y-3">
          {caso.leitura_painel && (
            <div className="bg-[#0d1a26] border border-[#1e3a5f] rounded-xl px-3 py-2 text-xs text-blue-200 flex flex-wrap gap-x-4 gap-y-1" data-testid="diag-leitura-painel">
              <span className="font-bold">🧭 Leitura do painel:</span>
              {caso.leitura_painel.sistema && <span>Sistema: <strong>{caso.leitura_painel.sistema}</strong></span>}
              {caso.leitura_painel.altura && <span>Painel: <strong>{caso.leitura_painel.altura}</strong></span>}
              {caso.leitura_painel.altura_corte_m > 0 && <span>Altura do corte: <strong>~{caso.leitura_painel.altura_corte_m} m</strong></span>}
            </div>
          )}
          {caso.demarcacao_resumo && caso.demarcacao_resumo.consumido_pct != null && (
            <div className="bg-[#0d2b33] border border-cyan-800 rounded-xl px-3 py-2 text-xs text-cyan-200 flex flex-wrap gap-x-4 gap-y-1" data-testid="diag-demarcacao-resumo">
              <span className="font-bold">📐 Demarcação:</span>
              <span>Consumido: <strong>{caso.demarcacao_resumo.consumido_pct}%{caso.demarcacao_resumo.consumido_cm ? ` (≈${caso.demarcacao_resumo.consumido_cm} cm)` : ""}</strong></span>
              <span>A consumir: <strong>{caso.demarcacao_resumo.a_consumir_pct}%</strong></span>
              {caso.demarcacao_resumo.anotacoes > 0 && <span>✏️ {caso.demarcacao_resumo.anotacoes} anotação(ões)</span>}
              {caso.demarcacao?.angulos?.l2 && <span>∠ corte: {caso.demarcacao.angulos.l2.inicial}°/{caso.demarcacao.angulos.l2.final}°</span>}
            </div>
          )}
          {caso.photo_base64 && (
            <div className={extrasCaso.length ? "grid grid-cols-2 gap-2" : ""}>
              <img src={`data:image/jpeg;base64,${caso.photo_base64}`} alt="Foto analisada" className="rounded-xl max-h-72 object-contain bg-black w-full" data-testid="diag-foto" />
              {extrasCaso.map((f, i) => (
                <div key={i} className="relative">
                  <img src={`data:image/jpeg;base64,${f.b64}`} alt={`Foto extra ${i + 1}`} className="rounded-xl max-h-72 object-contain bg-black w-full" data-testid={`diag-foto-extra-${i}`} />
                  <span className="absolute bottom-1 left-1 text-[9px] bg-black/70 text-white px-1.5 py-0.5 rounded">{FOCO_LABELS[f.foco] || f.foco}</span>
                </div>
              ))}
            </div>
          )}
          {caso.tipo === "painel" && caso.photo_base64 && (
            <button onClick={() => setDemarcAberta(true)} className="w-full rounded-xl py-2.5 text-xs font-extrabold border-2 border-cyan-600 bg-[#0d2b33] text-cyan-200" data-testid="diag-demarcar-btn">
              📐 {caso.demarcacao ? "Editar demarcação / anotações" : "Demarcar painel · medir · destacar pontos"}
            </button>
          )}
          {sanidade && (
            <div className="space-y-2" data-testid="diag-sanidade-blocos">
              {caso.visualizacao && <div className="bg-[#1b1b1b] border border-[#333] rounded-xl p-3 text-xs" data-testid="diag-visualizacao"><span className="font-bold">👁️ Visualização: </span>{caso.visualizacao}</div>}
              {(caso.hipoteses || []).length > 0 && (
                <div className="bg-purple-950/60 border border-purple-800 rounded-xl p-3 space-y-1" data-testid="diag-hipoteses">
                  <div className="text-xs font-bold text-purple-200">🔬 Diagnóstico (hipóteses):</div>
                  {caso.hipoteses.map((h, i) => (
                    <div key={i} className="text-xs flex items-start gap-2">
                      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold flex-shrink-0 ${h.certeza === "ALTA" ? "bg-green-900 text-green-200" : h.certeza === "MEDIA" ? "bg-amber-900 text-amber-200" : "bg-[#333] text-gray-300"}`}>{h.certeza || "?"}</span>
                      <span><strong>{h.doenca}</strong>{h.sinais ? ` — ${h.sinais}` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
              {caso.contraste && <div className="bg-green-950/60 border border-green-800 rounded-xl p-3 text-xs" data-testid="diag-contraste"><span className="font-bold">📖 Contraste com a literatura: </span>{caso.contraste}</div>}
              {(caso.recomendacoes || []).length > 0 && (
                <div className="bg-[#0d1a26] border border-[#1e3a5f] rounded-xl p-3 text-xs" data-testid="diag-recomendacoes">
                  <div className="font-bold text-blue-200 mb-1">✅ Recomendações de ação:</div>
                  <ul className="list-disc ml-4 space-y-0.5">{caso.recomendacoes.map((r, i) => <li key={i}>{r}</li>)}</ul>
                </div>
              )}
              {fichasSanidade.length > 0 && (
                <div>
                  <div className={`${SECAO} mb-1`}>📖 Fichas da biblioteca para comparar</div>
                  <div className="flex gap-1.5 flex-wrap">
                    {fichasSanidade.map((f) => (
                      <button key={f.id} onClick={() => setFichaVer(f)} className="text-[10px] px-2 py-1 rounded-full border border-green-700 bg-green-950 text-green-200 font-semibold" data-testid={`diag-ficha-btn-${f.id}`}>📖 {f.titulo.split("(")[0].split("/")[0].trim().slice(0, 30)}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="text-sm whitespace-pre-wrap text-gray-200 bg-[#1b1b1b] border border-[#2a2a2a] rounded-xl p-3" data-testid="diag-texto-ia">{renderBold(caso.ai_texto)}</div>
        </div>

        <div className="bg-[#0d1a26] text-[#93c5fd] text-[11px] font-bold px-3 py-2 text-center border-y border-[#1e3a5f]">
          {pendente ? (sanidade ? "🎯 Calibração: o diagnóstico de sanidade está certo?" : "🎯 Calibração: confirme ou corrija cada critério — cada correção ensina a IA") : `✅ Calibrado — IA acertou ${caso.acertos}/${caso.total_criterios}`}
        </div>
        <div className="p-3 space-y-3">
          {itensCalibracao.map((cr) => {
            const corr = pendente ? correcoes[cr.id] : (caso.correcoes || []).find((c) => c.id === cr.id);
            return (
              <div key={cr.id} className={`border rounded-xl p-3 ${STATUS_CORES[cr.status] || "bg-[#151d15] border-[#2e3e2e]"}`} data-testid={`diag-criterio-${cr.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <span className="font-semibold text-sm">{cr.nome}</span>
                  <span className="text-xs">{STATUS_LABEL[cr.status] || cr.status}{cr.nota != null ? ` · ${cr.nota}/10` : ""}</span>
                </div>
                <div className="text-xs mt-1">{cr.leitura}</div>
                {!sanidade && fichasPara(cr.id).length > 0 && (
                  <div className="flex gap-1 flex-wrap mt-1.5">
                    {fichasPara(cr.id).map((f) => (
                      <button key={f.id} onClick={() => setFichaVer(f)} className="text-[10px] px-2 py-0.5 rounded-full border border-green-700 bg-green-950 text-green-200" data-testid={`diag-ficha-${cr.id}-${f.id}`}>📖 {f.titulo.split("(")[0].split("—")[0].trim().slice(0, 28)}</button>
                    ))}
                  </div>
                )}
                {pendente && corr && (
                  <div className="mt-2 space-y-2">
                    <div className="flex gap-2">
                      <button onClick={() => setCorrecoes((p) => ({ ...p, [cr.id]: { ...p[cr.id], ia_acertou: true } }))}
                        className={`text-xs px-3 py-1.5 rounded-full border-2 font-bold ${corr.ia_acertou ? "bg-green-700 text-white border-green-500" : "bg-[#1b1b1b] text-[#999] border-[#333]"}`}
                        data-testid={`diag-acertou-${cr.id}`}>✓ IA acertou</button>
                      <button onClick={() => setCorrecoes((p) => ({ ...p, [cr.id]: { ...p[cr.id], ia_acertou: false } }))}
                        className={`text-xs px-3 py-1.5 rounded-full border-2 font-bold ${!corr.ia_acertou ? "bg-red-700 text-white border-red-500" : "bg-[#1b1b1b] text-[#999] border-[#333]"}`}
                        data-testid={`diag-corrigir-${cr.id}`}>✗ Corrigir</button>
                    </div>
                    {!corr.ia_acertou && (
                      <textarea value={corr.correcao} onChange={(e) => setCorrecoes((p) => ({ ...p, [cr.id]: { ...p[cr.id], correcao: e.target.value } }))}
                        placeholder="Qual é a leitura correta? (isso calibra a IA)" rows={2}
                        className={`${INPUT} text-xs`} data-testid={`diag-correcao-texto-${cr.id}`} />
                    )}
                  </div>
                )}
                {!pendente && corr && !corr.ia_acertou && (
                  <div className="mt-1 text-xs bg-black/30 rounded p-2"><strong>Correção:</strong> {corr.correcao}</div>
                )}
              </div>
            );
          })}
          {pendente && (
            <>
              <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} placeholder="Comentário geral (opcional)" rows={2}
                className={INPUT} data-testid="diag-comentario" />
              <Button onClick={salvarCalibracao} disabled={salvando} className="w-full bg-green-700 hover:bg-green-800 font-extrabold" data-testid="diag-salvar-calibracao-btn">
                {salvando ? "Salvando..." : "💾 Salvar calibração"}
              </Button>
            </>
          )}
          <button onClick={() => excluir(caso.id)} className="w-full rounded-xl py-2 text-xs font-bold border border-red-900 bg-red-950/50 text-red-300" data-testid="diag-excluir-btn">🗑️ Excluir caso</button>
        </div>
        {fichaVer && <FichaReferenciaModal ficha={fichaVer} fotoUsuario={caso?.photo_base64 || null} onClose={() => setFichaVer(null)} />}
        {demarcAberta && (
          <DemarcacaoEditor
            foto={`data:image/jpeg;base64,${caso.photo_base64}`}
            inicial={caso.demarcacao || null}
            onClose={() => setDemarcAberta(false)}
            onSave={salvarDemarcacaoCaso}
          />
        )}
      </div>
    );
  }

  // ---------- Vista principal ----------
  return (
    <div className="bg-[#111] text-white rounded-2xl overflow-hidden -mx-1 sm:mx-0" data-testid="diagnostico-ia-panel">
      {showCamera && (
        <CameraGuiada
          tipo={tipo}
          modoInicial={cameraModoInicial}
          onClose={() => setShowCamera(false)}
          onCapture={(dataUrl, modo) => {
            setShowCamera(false);
            if (modo?.id) setFoco(modo.id);
            addFoto(dataUrl, modo?.id || foco, modo?.anguloMedido ?? null);
            setObs((o) => o || `Foco da foto: ${modo?.label || ""}`.trim());
          }}
        />
      )}
      <div className="flex items-center justify-between px-3.5 py-3 bg-[#1a241a]">
        <b className="text-sm">🔬 Diagnóstico IA</b>
        <span className="text-[9px] bg-green-700 px-2 py-1 rounded-full font-extrabold">MÓDULO 1 · CALIBRAÇÃO</span>
      </div>
      <div className="bg-[#2a3a2a] text-[#c9eec9] text-[11px] px-3 py-1.5 text-center font-semibold">
        Fotografe painel ou folhagem → IA analisa → você confirma ou corrige → o sistema aprende
      </div>

      {!online && (
        <div className="bg-amber-950 border-b border-amber-800 text-amber-200 text-[11px] font-semibold px-3 py-2 text-center" data-testid="diag-offline-banner">
          📴 Sem conexão — pode fotografar normalmente: as fotos ficam salvas no aparelho e você envia para aprovação quando o sinal voltar.
        </div>
      )}

      <div className="p-3 space-y-3">
        {stats && (
          <div className="grid grid-cols-4 gap-2" data-testid="diag-stats">
            {[[stats.total, "Análises", "text-white"], [stats.calibrados, "Calibradas", "text-green-400"], [stats.pendentes, "Pendentes", "text-amber-400"], [stats.acerto_geral_pct != null ? `${stats.acerto_geral_pct}%` : "—", "Acerto IA", "text-blue-400"]].map(([v, l, c]) => (
              <div key={l} className={`${CARD} p-2 text-center`}>
                <div className={`text-lg font-extrabold ${c}`}>{v}</div>
                <div className="text-[9.5px] text-[#999] font-semibold">{l}</div>
              </div>
            ))}
          </div>
        )}

        {stats?.por_criterio?.length > 0 && (
          <div className={`${CARD} p-3`}>
            <button onClick={() => setStatsAberto((v) => !v)} className="w-full text-left flex items-center justify-between" data-testid="diag-stats-criterio-toggle">
              <span className={SECAO}>📊 Acerto da IA por critério</span>
              <span className="text-[#777] text-xs">{statsAberto ? "▲" : "▼"}</span>
            </button>
            {statsAberto && (
              <div className="space-y-1.5 mt-2">
                {stats.por_criterio.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 text-xs">
                    <span className="w-44 truncate text-gray-300">{c.nome}</span>
                    <div className="flex-1 bg-[#1b1b1b] rounded-full h-2"><div className={`h-2 rounded-full ${c.pct >= 80 ? "bg-green-500" : c.pct >= 50 ? "bg-yellow-500" : "bg-red-500"}`} style={{ width: `${c.pct}%` }} /></div>
                    <span className="w-20 text-right text-[#999] whitespace-nowrap">{c.acertos}/{c.total} ({c.pct}%)</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ---------- Nova análise ---------- */}
        <div className={`${CARD} overflow-hidden`}>
          <div className="px-3 pt-3 pb-1"><span className={SECAO}>1 · O que você vai analisar</span></div>
          <div className="flex gap-1.5 px-3 py-2 overflow-x-auto">
            {[["painel", "🌳 Painel"], ["folhagem", "🍃 Diagnóstico Foliar"]].map(([v, l]) => (
              <button key={v} onClick={() => { setTipo(v); setFoco(v === "painel" ? "geral" : "folha"); setGateErro(null); setFotos([]); setComplementos([]); setDemarcPend(null); }}
                className={chip(tipo === v, "border-green-500 text-green-300 bg-green-950")}
                data-testid={`diag-tipo-${v}`}>{l}</button>
            ))}
            {tipo === "painel" && [["rotina", "✂️ Rotina"], ["teste_fitossanitario", "🔬 Teste fitossanitário"]].map(([v, l]) => (
              <button key={v} onClick={() => setContexto(v)}
                className={chip(contexto === v, "border-purple-500 text-purple-300 bg-purple-950")}
                data-testid={`diag-contexto-${v}`}>{l}</button>
            ))}
          </div>

          {tipo === "painel" && (
            <>
              <div className="px-3 pt-1 pb-1"><span className={SECAO}>2 · Sistema de sangria (melhora a leitura do consumo)</span></div>
              <div className="flex gap-1.5 px-3 py-2 overflow-x-auto">
                {[["", "Não informar"], ["d/2", "d/2"], ["d/3", "d/3"], ["d/4", "d/4"], ["d/5", "d/5"], ["d/6", "d/6"], ["d/7", "d/7"]].map(([v, l]) => (
                  <button key={v || "nd"} onClick={() => setSistemaSangria(v)}
                    className={chip(sistemaSangria === v, "border-amber-500 text-amber-300 bg-amber-950")}
                    data-testid={`diag-sistema-${v || "nd"}`}>{l}</button>
                ))}
              </div>
            </>
          )}

          <div className="px-3 pt-1 pb-1"><span className={SECAO}>{tipo === "painel" ? "3" : "2"} · Foco da foto</span></div>
          <div className="flex gap-1.5 px-3 py-2 overflow-x-auto">
            {FOCOS[tipo].map(([v, l]) => (
              <button key={v} onClick={() => setFoco(v)}
                className={chip(foco === v, "border-blue-500 text-blue-300 bg-blue-950")}
                data-testid={`diag-foco-${v}`}>{l}</button>
            ))}
          </div>

          <div className="px-3 py-2 grid grid-cols-2 gap-2">
            <input value={clone} onChange={(e) => setClone(e.target.value)} placeholder="Clone (opcional)" className={INPUT} data-testid="diag-clone-input" />
            <input value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Observação (opcional)" className={INPUT} data-testid="diag-obs-input" />
          </div>

          <div className="px-3 pb-2">
            <button onClick={() => setGuiaAberto(!guiaAberto)} className="w-full text-left text-[11px] font-bold text-blue-300 bg-[#0d1a26] border border-[#1e3a5f] rounded-xl px-3 py-2" data-testid="diag-guia-toggle">
              📷 Como fotografar para cada critério {guiaAberto ? "▲" : "▼"}
            </button>
            {guiaAberto && (
              <div className="bg-[#0d1a26] border border-[#1e3a5f] rounded-xl p-3 space-y-1.5 mt-1.5" data-testid="diag-guia-conteudo">
                {GUIA_FOTOS[tipo].map(([t, d]) => (
                  <div key={t} className="text-[11px] text-gray-300"><strong className="text-blue-200">{t}:</strong> {d}</div>
                ))}
              </div>
            )}
          </div>

          <div className="px-3 pt-1 pb-1"><span className={SECAO}>{tipo === "painel" ? "4" : "3"} · Foto (máx. 3 por análise)</span></div>
          <div className="px-3 py-2 space-y-2">
            <label className={`flex items-start gap-2 text-[11px] font-semibold rounded-xl px-3 py-2 cursor-pointer border-2 ${modoLivre ? "bg-purple-950 border-purple-500 text-purple-200" : "bg-[#1b1b1b] border-[#333] text-[#999]"}`} data-testid="diag-modo-livre">
              <input type="checkbox" checked={modoLivre} onChange={(e) => setModoLivre(e.target.checked)} className="mt-0.5" data-testid="diag-modo-livre-check" />
              <span>🧪 Modo calibração livre — aceitar qualquer imagem <strong>sem validar o enquadramento</strong> (ex.: foto avulsa da galeria)</span>
            </label>

            <div className="flex gap-2">
              <button type="button" disabled={fotos.length >= 3 || validando} onClick={() => { setCameraModoInicial(foco); setShowCamera(true); }}
                className="flex-1 rounded-xl py-2.5 text-xs font-extrabold border-2 border-green-600 bg-green-950 text-green-200 disabled:opacity-40" data-testid="diag-camera-btn">📸 Câmera com guia</button>
              <button type="button" disabled={fotos.length >= 3 || validando} onClick={() => fileRef.current?.click()}
                className="flex-1 rounded-xl py-2.5 text-xs font-extrabold border-2 border-[#444] bg-[#1b1b1b] text-gray-300 disabled:opacity-40" data-testid="diag-galeria-btn">🖼️ Galeria</button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="diag-foto-input" />

            {validando && (
              <div className="text-center text-[11px] font-semibold text-blue-300 animate-pulse py-1" data-testid="diag-validando">🎯 Conferindo o enquadramento da foto...</div>
            )}

            {fotos.length > 0 && (
              <div className="grid grid-cols-3 gap-2" data-testid="diag-foto-preview">
                {fotos.map((f, i) => (
                  <div key={i} className="relative">
                    <img src={f.dataUrl} alt={`Foto ${i + 1}`} className="rounded-xl h-24 w-full object-cover" data-testid={`diag-foto-thumb-${i}`} />
                    <span className="absolute bottom-1 left-1 text-[9px] bg-black/70 text-white px-1.5 py-0.5 rounded">{f.semValidacao ? "🧪" : "✓"} {FOCO_LABELS[f.foco] || f.foco}</span>
                    <button onClick={() => { setFotos((p) => p.filter((_, j) => j !== i)); if (i === 0) setDemarcPend(null); }} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 text-xs leading-none" data-testid={`diag-foto-remove-${i}`}>✕</button>
                  </div>
                ))}
              </div>
            )}

            {tipo === "painel" && fotos.length > 0 && (
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setDemarcAberta(true)}
                  className="flex-1 rounded-xl py-2.5 text-xs font-extrabold border-2 border-cyan-600 bg-[#0d2b33] text-cyan-200" data-testid="diag-demarcar-pre-btn">
                  📐 {demarcPend ? "Editar demarcação do painel" : "Demarcar painel (opcional)"}
                </button>
                {demarcPend?.consumido_pct != null && (
                  <span className="text-[11px] font-extrabold text-cyan-200 bg-[#0d2b33] border border-cyan-800 rounded-xl px-2 py-2 whitespace-nowrap" data-testid="diag-demarc-pend-pct">
                    {demarcPend.consumido_pct}% consumido
                  </span>
                )}
              </div>
            )}

            {fotos.find((f) => f.anguloMedido != null) && (
              <div className="text-[11px] font-bold text-green-200 bg-green-950 border border-green-800 rounded-xl px-3 py-2" data-testid="diag-angulo-medido">
                📐 Ângulo medido com a guia: {fotos.find((f) => f.anguloMedido != null).anguloMedido}° {fotos.find((f) => f.anguloMedido != null).anguloMedido >= 30 && fotos.find((f) => f.anguloMedido != null).anguloMedido <= 36 ? "✅ dentro do padrão (33°)" : "⚠️ fora do padrão (33°)"}
              </div>
            )}

            {complementos.length > 0 && fotos.length > 0 && fotos.length < 3 && (
              <div className="bg-[#0d1a26] border border-[#1e3a5f] rounded-xl p-3 space-y-2" data-testid="diag-complementos">
                <div className="text-[11px] font-bold text-blue-200">📸 Para a análise completa, o sistema recomenda mais {complementos.length === 1 ? "1 foto" : `${complementos.length} fotos`}:</div>
                {complementos.map((c) => (
                  <div key={c.foco} className="flex items-center justify-between gap-2">
                    <div className="text-[11px] text-blue-100 flex-1"><strong>{FOCO_LABELS[c.foco] || c.foco}</strong>{c.motivo ? ` — ${c.motivo}` : ""}</div>
                    <button onClick={() => { setFoco(c.foco); setCameraModoInicial(c.foco); setShowCamera(true); }} className="text-[11px] px-3 py-1.5 rounded-full bg-blue-700 text-white font-bold whitespace-nowrap" data-testid={`diag-complemento-${c.foco}`}>📸 Tirar</button>
                  </div>
                ))}
                <div className="text-[10px] text-blue-300">Se preferir, pode analisar só com as fotos atuais.</div>
              </div>
            )}

            {gateErro && (
              <div className="bg-amber-950 border border-amber-800 rounded-xl p-3 text-[11px] text-amber-200" data-testid="diag-gate-erro">
                <div className="font-bold">📷 Foto reprovada no enquadramento — não foi analisada nem salva</div>
                {gateErro.motivo && <div className="mt-1"><strong>Motivo:</strong> {gateErro.motivo}</div>}
                {gateErro.orientacao && <div className="mt-1">💡 <strong>Como refazer:</strong> {gateErro.orientacao}</div>}
              </div>
            )}

            {analisando ? (
              <div className="text-center py-3" data-testid="diag-loading">
                <div className="animate-pulse text-sm font-semibold text-green-300">{ETAPAS_LOADING[etapa]}</div>
                <div className="text-[10px] text-[#777] mt-1">A análise leva ~30 segundos</div>
              </div>
            ) : (
              <Button onClick={analisar} disabled={!fotos.length || validando} className="w-full bg-green-700 hover:bg-green-800 font-extrabold" data-testid="diag-analisar-btn">🔬 Analisar com IA{fotos.length > 1 ? ` (${fotos.length} fotos)` : ""}</Button>
            )}
          </div>
        </div>

        {filaOffline.length > 0 && (
          <div className="bg-amber-950/60 border border-amber-800 rounded-2xl p-3 space-y-2" data-testid="diag-fila-offline">
            <div className="font-bold text-sm text-amber-200">📴 Fotos salvas no aparelho ({filaOffline.length})</div>
            <div className="text-[10px] text-amber-300/80">Tiradas sem conexão. Envie para aprovação quando estiver com sinal{modoLivre ? " — com o modo livre ativo, entram sem validação" : ""}.</div>
            {filaOffline.map((item) => (
              <div key={item.id} className="flex items-center gap-2 border border-amber-900 bg-black/30 rounded-xl p-2" data-testid={`diag-fila-item-${item.id}`}>
                <img src={item.dataUrl} alt="Foto offline" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate">{item.tipo === "painel" ? "🌳 Painel" : "🍃 Foliar"} · {FOCO_LABELS[item.foco] || item.foco}</div>
                  <div className="text-[10px] text-[#999]">{new Date(item.criadoEm).toLocaleString("pt-BR")}{item.anguloMedido != null ? ` · 📐 ${item.anguloMedido}°` : ""}</div>
                </div>
                <Button size="sm" disabled={!online || enviandoFilaId === item.id || validando} onClick={() => enviarDaFila(item)} className="bg-green-700 hover:bg-green-800 text-xs h-8" data-testid={`diag-fila-enviar-${item.id}`}>{enviandoFilaId === item.id ? "..." : "📤 Enviar"}</Button>
                <button onClick={() => excluirDaFila(item.id)} className="text-red-400 text-sm px-1" data-testid={`diag-fila-excluir-${item.id}`}>🗑️</button>
              </div>
            ))}
          </div>
        )}

        <div className={`${CARD} p-3 space-y-2`}>
          <button onClick={() => setShowBiblio((v) => !v)} className="w-full text-left flex items-center justify-between" data-testid="diag-biblioteca-toggle">
            <span className="font-bold text-sm text-green-300">📖 Biblioteca de referências ({referencias.length})</span>
            <span className="text-[#777] text-xs">{showBiblio ? "▲" : "▼"}</span>
          </button>
          <p className="text-[10.5px] text-[#999]">Fichas da literatura usadas pela IA no contraste de cada diagnóstico (técnica, doenças de painel, radiculares e foliares). Editáveis — o que você mudar vale nas próximas análises.</p>
          {showBiblio && <div className="bg-white rounded-xl p-2"><ReferenciasBiblioteca referencias={referencias} onChange={carregar} onVerFicha={setFichaVer} /></div>}
        </div>

        <div className={`${CARD} p-3`}>
          <div className="font-bold text-sm text-green-300 mb-2">📁 Histórico de análises</div>
          {casos.length === 0 && <div className="text-xs text-[#777]">Nenhuma análise ainda. Envie a primeira foto acima.</div>}
          <div className="space-y-2">
            {casos.map((c) => (
              <button key={c.id} onClick={() => abrirCaso(c.id)} className="w-full text-left border border-[#2e3e2e] bg-[#1b1b1b] hover:bg-[#222b22] rounded-xl p-3 flex items-center justify-between gap-2" data-testid={`diag-caso-${c.id}`}>
                <div>
                  <div className="text-sm font-semibold">{c.tipo === "painel" ? "🌳 Painel" : "🍃 Diagnóstico Foliar"}{c.clone ? ` · ${c.clone}` : ""}</div>
                  <div className="text-[10px] text-[#888]">{new Date(c.created_at).toLocaleString("pt-BR")}</div>
                </div>
                <div className="text-right">
                  {c.nota_geral != null && <div className="text-xs font-bold text-green-400">{c.nota_geral}/10</div>}
                  {c.demarcacao_resumo?.consumido_pct != null && <div className="text-[9px] font-bold text-cyan-400" data-testid={`diag-caso-demarc-${c.id}`}>📐 {c.demarcacao_resumo.consumido_pct}% consumido</div>}
                  {c.modo === "sanidade" && <div className="text-[9px] font-bold text-purple-400">🔬 sanidade</div>}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${c.status === "calibrado" ? "bg-green-950 text-green-300 border-green-800" : "bg-amber-950 text-amber-300 border-amber-800"}`}>
                    {c.status === "calibrado" ? `✅ ${c.acertos}/${c.total_criterios}` : "⏳ pendente"}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      {fichaVer && <FichaReferenciaModal ficha={fichaVer} fotoUsuario={null} onClose={() => setFichaVer(null)} />}
      {demarcAberta && fotos[0] && (
        <DemarcacaoEditor
          foto={fotos[0].dataUrl}
          inicial={demarcPend}
          onClose={() => setDemarcAberta(false)}
          onSave={(d) => { setDemarcPend(d); setDemarcAberta(false); toast.success(`📐 Demarcação pronta — será salva junto com a análise (${d.consumido_pct != null ? d.consumido_pct + "% consumido" : "sem %"})`); }}
        />
      )}
    </div>
  );
}
